import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomersPanel } from "@/components/staff/CustomersPanel";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { OrdersBoard } from "@/components/staff/OrdersBoard";
import { MenuManager } from "@/components/staff/MenuManager";
import { SettingsPanel } from "@/components/staff/SettingsPanel";
import { OrderAlerts } from "@/components/staff/OrderAlerts";
import { QrStickers } from "@/components/staff/QrStickers";
import { AttendancePanel } from "@/components/staff/attendance/AttendancePanel";
import { ReportsPanel } from "@/components/staff/DailyReport";
import { PosPanel } from "@/components/staff/pos/PosPanel";
import { ClosingHistory } from "@/components/staff/ClosingHistory";

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/staf/login", { replace: true });
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const tab = "data-[state=active]:bg-forest data-[state=active]:text-paper rounded-lg px-4";
  const cashier = user.role === "admin" || (user.division || "").toLowerCase().includes("kasir");
  return (
    <div className="min-h-screen bg-cream" data-testid="staff-dashboard">
      <header className="no-print bg-paper border-b border-sage-200">
        <div className="wrap h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo small /><span className="hidden sm:inline text-xs font-semibold tracking-[0.15em] text-forest bg-sage-100 px-3 py-1 rounded-full">PANEL STAF</span></div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-muted2" data-testid="staff-user-name">{user.name} · {user.role === "admin" ? "Admin" : "Staf"}</span>
            <OrderAlerts />
            <button onClick={logout} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sage-200 hover:bg-sage-100" data-testid="staff-logout-button"><LogOut className="w-4 h-4" /> Keluar</button>
          </div>
        </div>
      </header>
      <main className="wrap py-8">
        <Tabs defaultValue="orders">
          <TabsList className="no-print bg-sage-100 p-1 h-auto flex-wrap">
            <TabsTrigger value="orders" className={tab} data-testid="staff-tab-orders">Pesanan</TabsTrigger>
            {cashier && <TabsTrigger value="kasir" className={tab} data-testid="staff-tab-cashier">Kasir</TabsTrigger>}
            <TabsTrigger value="menu" className={tab} data-testid="staff-tab-menu">Menu</TabsTrigger>
            <TabsTrigger value="qr" className={tab} data-testid="staff-tab-qr">Stiker QR</TabsTrigger>
            <TabsTrigger value="absensi" className={tab} data-testid="staff-tab-attendance">Absensi</TabsTrigger>
            {cashier && <TabsTrigger value="laporan" className={tab} data-testid="staff-tab-report">Laporan</TabsTrigger>}
            {user.role === "admin" && <TabsTrigger value="pelanggan" className={tab} data-testid="staff-tab-customers">Pelanggan</TabsTrigger>}
            {user.role === "admin" && <TabsTrigger value="settings" className={tab} data-testid="staff-tab-settings">Pengaturan</TabsTrigger>}
          </TabsList>
          <TabsContent value="orders" className="mt-6"><OrdersBoard /></TabsContent>
          {cashier && <TabsContent value="kasir" className="mt-6"><PosPanel /></TabsContent>}
          <TabsContent value="menu" className="mt-6"><MenuManager /></TabsContent>
          <TabsContent value="qr" className="mt-6"><QrStickers /></TabsContent>
          <TabsContent value="absensi" className="mt-6"><AttendancePanel /></TabsContent>
          {cashier && <TabsContent value="laporan" className="mt-6">{user.role === "admin" ? <ReportsPanel /> : <ClosingHistory />}</TabsContent>}
          {user.role === "admin" && <TabsContent value="pelanggan" className="mt-6"><CustomersPanel /></TabsContent>}
          {user.role === "admin" && <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}
